/**
 * Iter 67eh — Public /widget-builder page.
 *
 * Anyone — public visitor or signed-in vendor — can build a copy-paste
 * iframe snippet for the RentalCar.al search widget. Live preview on the
 * right; config form on the left.
 *
 *   • affiliate_id (optional)  — for attribution on resulting bookings
 *   • country pre-fill
 *   • pickup_location pre-fill
 *   • accent color
 *   • variant compact / full
 *   • width / height
 */
import { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Code2,
  Copy,
  Check,
  Palette,
  Globe,
  Tag,
  Sparkles,
  TrendingUp,
  Trophy,
  Eye,
  EyeOff,
  Save,
} from "lucide-react";

const AFFIL_RE = /[^a-zA-Z0-9_-]/g;
function _cleanAffiliate(raw) {
  return String(raw || "").replace(AFFIL_RE, "").replace(/^[-_]+|[-_]+$/g, "").slice(0, 60);
}

const COUNTRIES = [
  { key: "", label: "— any —" },
  { key: "Albania", label: "Albania" },
  { key: "Kosovo", label: "Kosovo" },
  { key: "North Macedonia", label: "North Macedonia" },
  { key: "Montenegro", label: "Montenegro" },
  { key: "Greece", label: "Greece" },
];

const ACCENT_PRESETS = [
  { hex: "f59e0b", name: "Amber (default)" },
  { hex: "ef4444", name: "Red" },
  { hex: "0ea5e9", name: "Sky" },
  { hex: "10b981", name: "Emerald" },
  { hex: "8b5cf6", name: "Violet" },
  { hex: "111827", name: "Black" },
];

export default function WidgetBuilderPage() {
  const [affiliateId, setAffiliateId] = useState("");
  const [country, setCountry] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [accent, setAccent] = useState("f59e0b");
  const [variant, setVariant] = useState("full");
  const [width, setWidth] = useState("100%");
  const [height, setHeight] = useState(420);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);

  // Iter 67ei — public-profile claim state. We auto-detect whether the
  // current affiliate id already has a profile, and load any stored edit
  // token from localStorage so the embedder can update later without
  // remembering the token themselves.
  const [profile, setProfile] = useState(null);  // server's current public profile (or null)
  const [editToken, setEditToken] = useState("");  // local edit token (from localStorage or fresh claim)
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimSaving, setClaimSaving] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [website, setWebsite] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [tagline, setTagline] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [justClaimedToken, setJustClaimedToken] = useState(null);

  // Origin of the live deployment; embedders need a fully-qualified URL.
  const origin = useMemo(() => {
    try { return window.location.origin; } catch { return "https://rentalcar.al"; }
  }, []);

  // Live affiliate stats (best-effort) — gives the embedder a sense their
  // ID is "live" and tracks conversions. Errors silently if backend is down.
  useEffect(() => {
    const cleaned = _cleanAffiliate(affiliateId);
    if (!cleaned) { setStats(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/affiliate-stats", { params: { affiliate_id: cleaned, days: 30 } });
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) setStats(null);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [affiliateId]);

  // Iter 67ei — detect existing public profile + load any saved edit token.
  useEffect(() => {
    const cleaned = _cleanAffiliate(affiliateId);
    if (!cleaned) {
      setProfile(null);
      setEditToken("");
      return;
    }
    let cancelled = false;
    let storedToken = "";
    try { storedToken = localStorage.getItem(`rentalcar:edit_token:${cleaned}`) || ""; } catch { /* ignore */ }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/public/affiliate-profile/${encodeURIComponent(cleaned)}`);
        if (cancelled) return;
        setProfile(data);
        setEditToken(storedToken);
        setDisplayName(data.display_name || "");
        setWebsite(data.website || "");
        setLogoUrl(data.logo_url || "");
        setTagline(data.tagline || "");
        setIsPublic(!!data.is_public);
      } catch {
        if (!cancelled) {
          setProfile(null);
          setEditToken("");
          // Reset fields only if the panel isn't open — don't trash the user's draft.
          if (!claimOpen) {
            setDisplayName(""); setWebsite(""); setLogoUrl(""); setTagline(""); setIsPublic(true);
          }
        }
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateId]);

  const saveProfile = async () => {
    setClaimError(null);
    const aid = _cleanAffiliate(affiliateId);
    if (!aid) { setClaimError("Pick an affiliate ID first."); return; }
    if (!displayName.trim()) { setClaimError("A display name is required."); return; }
    setClaimSaving(true);
    try {
      if (profile && editToken) {
        // Update existing
        const { data } = await api.patch(`/public/affiliate-profile/${encodeURIComponent(aid)}`, {
          edit_token: editToken,
          display_name: displayName.trim(),
          website: website.trim() || null,
          logo_url: logoUrl.trim() || null,
          tagline: tagline.trim() || null,
          is_public: isPublic,
        });
        setProfile(data);
        setJustClaimedToken(null);
      } else {
        // Fresh claim
        const { data } = await api.post(`/public/affiliate-profile`, {
          affiliate_id: aid,
          display_name: displayName.trim(),
          website: website.trim() || null,
          logo_url: logoUrl.trim() || null,
          tagline: tagline.trim() || null,
          is_public: isPublic,
        });
        if (data.edit_token) {
          try { localStorage.setItem(`rentalcar:edit_token:${aid}`, data.edit_token); } catch { /* ignore */ }
          setEditToken(data.edit_token);
          setJustClaimedToken(data.edit_token);
        }
        const { edit_token: _unused, ...publicShape } = data;
        void _unused;
        setProfile(publicShape);
      }
    } catch (e) {
      setClaimError(e?.response?.data?.detail || "Could not save profile.");
    } finally {
      setClaimSaving(false);
    }
  };

  const embedUrl = useMemo(() => {
    const u = new URL(`${origin}/embed/search`);
    const aid = _cleanAffiliate(affiliateId);
    if (aid) u.searchParams.set("affiliate_id", aid);
    if (country) u.searchParams.set("country", country);
    if (pickupLocation.trim()) u.searchParams.set("pickup_location", pickupLocation.trim());
    if (accent && accent !== "f59e0b") u.searchParams.set("accent", accent);
    if (variant !== "full") u.searchParams.set("variant", variant);
    return u.toString();
  }, [origin, affiliateId, country, pickupLocation, accent, variant]);

  const snippet = useMemo(() => {
    const w = String(width || "100%").trim();
    const h = parseInt(height, 10) || 420;
    return `<iframe
  src="${embedUrl}"
  width="${w}"
  height="${h}"
  frameborder="0"
  scrolling="no"
  style="border: 0; max-width: 100%; border-radius: 24px;"
  title="Book your car rental on RentalCar.al"
  loading="lazy"
></iframe>`;
  }, [embedUrl, width, height]);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-[11px] font-bold uppercase tracking-[0.18em] mb-4">
            <Sparkles className="h-3 w-3" /> Widget builder
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold leading-[1.05]" data-testid="widget-builder-title">
            Drop the RentalCar.al search<br />
            <span className="text-amber-600">on your own site</span>
          </h1>
          <p className="mt-4 text-neutral-600 max-w-2xl">
            Travel blog, hotel concierge page, airline partner site? Embed our
            search engine in one copy-paste. Add your{" "}
            <code className="px-1 bg-neutral-100 rounded text-xs">affiliate_id</code>{" "}
            and every booking through your widget is tracked back to you.
          </p>
          <Link
            to="/widget-builder/top-partners"
            className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-amber-700 hover:text-amber-800 transition"
            data-testid="widget-builder-leaderboard-link"
          >
            <Trophy className="h-4 w-4" /> See top partners →
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Config form */}
          <div className="lg:col-span-5 space-y-6" data-testid="widget-builder-form">
            {/* Affiliate ID */}
            <section>
              <Label className="flex items-center gap-2 text-sm font-semibold mb-2">
                <Tag className="h-4 w-4 text-amber-600" /> Affiliate ID <span className="text-neutral-400 font-normal">(optional)</span>
              </Label>
              <Input
                placeholder="e.g. albania-travel-blog"
                value={affiliateId}
                onChange={(e) => setAffiliateId(_cleanAffiliate(e.target.value))}
                className="rounded-xl"
                data-testid="widget-builder-affiliate-input"
              />
              <p className="text-[11px] text-neutral-500 mt-1.5">
                Lowercase letters, numbers, dashes and underscores. Persisted on every booking through your widget.
              </p>
              {stats && stats.bookings > 0 && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-800" data-testid="widget-builder-stats">
                  <TrendingUp className="h-3 w-3" /> {stats.bookings} booking{stats.bookings === 1 ? "" : "s"} from this ID in the last {stats.window_days} days
                </div>
              )}
            </section>

            {/* Iter 67ei — Public-leaderboard claim card */}
            {_cleanAffiliate(affiliateId) && (
              <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4" data-testid="widget-builder-claim-card">
                <div className="flex items-start gap-3 mb-3">
                  <Trophy className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">
                      {profile ? (
                        <>Public profile {profile.is_public ? <span className="text-emerald-700">· live</span> : <span className="text-neutral-500">· hidden</span>}</>
                      ) : (
                        <>Claim a spot on the public leaderboard</>
                      )}
                    </div>
                    <p className="text-[11px] text-neutral-600 mt-0.5">
                      {profile ? (
                        <>Update your listing on <Link to="/widget-builder/top-partners" className="text-amber-700 hover:underline">top partners</Link>. Edits authorise via the token cached on this device.</>
                      ) : (
                        <>Show your brand on <Link to="/widget-builder/top-partners" className="text-amber-700 hover:underline">top partners</Link> once your widget starts converting. Free brand mention from RentalCar.al.</>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setClaimOpen((v) => !v)}
                    className="text-xs font-semibold text-amber-800 hover:text-amber-900 underline shrink-0"
                    data-testid="widget-builder-claim-toggle"
                  >
                    {claimOpen ? "Hide" : (profile ? "Edit" : "Claim")}
                  </button>
                </div>

                {claimOpen && (
                  <div className="space-y-2 pl-1" data-testid="widget-builder-claim-form">
                    <Input
                      placeholder="Display name (e.g. Albania Travel Blog)"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="rounded-xl"
                      data-testid="widget-builder-claim-name"
                    />
                    <Input
                      placeholder="Website (https://yoursite.com)"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      className="rounded-xl"
                      data-testid="widget-builder-claim-website"
                    />
                    <Input
                      placeholder="Logo URL (optional, square PNG works best)"
                      value={logoUrl}
                      onChange={(e) => setLogoUrl(e.target.value)}
                      className="rounded-xl"
                      data-testid="widget-builder-claim-logo"
                    />
                    <Input
                      placeholder="Tagline (optional, 1 line)"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value.slice(0, 120))}
                      className="rounded-xl"
                      data-testid="widget-builder-claim-tagline"
                    />
                    <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer mt-1">
                      <input
                        type="checkbox"
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                        className="h-4 w-4 rounded border-neutral-300"
                        data-testid="widget-builder-claim-public"
                      />
                      {isPublic ? (
                        <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> Show on the public top-partners leaderboard</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-neutral-500"><EyeOff className="h-3 w-3" /> Keep my profile hidden (still tracks bookings)</span>
                      )}
                    </label>
                    <Button
                      onClick={saveProfile}
                      disabled={claimSaving}
                      className="rounded-xl bg-amber-500 hover:bg-amber-400 text-amber-950 w-full"
                      data-testid="widget-builder-claim-save"
                    >
                      {claimSaving ? "Saving…" : (
                        <><Save className="h-4 w-4 mr-1.5" /> {profile ? "Update profile" : "Claim this affiliate ID"}</>
                      )}
                    </Button>
                    {claimError && (
                      <div className="text-[11px] text-rose-600" data-testid="widget-builder-claim-error">{claimError}</div>
                    )}
                    {justClaimedToken && (
                      <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 p-2 rounded-lg" data-testid="widget-builder-claim-token-saved">
                        Profile claimed. Your edit token is stored on this device — clear localStorage on this browser and you'll lose edit access.
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* Pre-fill */}
            <section>
              <Label className="flex items-center gap-2 text-sm font-semibold mb-2">
                <Globe className="h-4 w-4 text-sky-600" /> Pre-fill
              </Label>
              <div className="grid grid-cols-1 gap-2">
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="rounded-xl border border-input bg-white px-3 py-2 text-sm"
                  data-testid="widget-builder-country"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
                <Input
                  placeholder="Pickup location (e.g. Tirana Airport)"
                  value={pickupLocation}
                  onChange={(e) => setPickupLocation(e.target.value)}
                  className="rounded-xl"
                  data-testid="widget-builder-pickup"
                />
              </div>
            </section>

            {/* Accent color */}
            <section>
              <Label className="flex items-center gap-2 text-sm font-semibold mb-2">
                <Palette className="h-4 w-4 text-violet-600" /> Accent color
              </Label>
              <div className="flex items-center gap-2 flex-wrap">
                {ACCENT_PRESETS.map((p) => (
                  <button
                    key={p.hex}
                    type="button"
                    onClick={() => setAccent(p.hex)}
                    className={`h-9 w-9 rounded-full transition ring-2 ${accent === p.hex ? "ring-neutral-900" : "ring-transparent hover:ring-neutral-200"}`}
                    style={{ background: `#${p.hex}` }}
                    title={p.name}
                    aria-label={p.name}
                    data-testid={`widget-builder-accent-${p.hex}`}
                  />
                ))}
                <div className="flex items-center gap-1.5 ml-2">
                  <span className="text-xs text-neutral-500">#</span>
                  <Input
                    value={accent}
                    onChange={(e) => setAccent(e.target.value.replace(/[^a-fA-F0-9]/g, "").slice(0, 6))}
                    className="rounded-xl w-24 font-mono text-xs"
                    data-testid="widget-builder-accent-hex"
                  />
                </div>
              </div>
            </section>

            {/* Variant */}
            <section>
              <Label className="text-sm font-semibold mb-2">Variant</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "full", label: "Full", desc: "With brand badge" },
                  { key: "compact", label: "Compact", desc: "Form only" },
                ].map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setVariant(v.key)}
                    className={`rounded-xl border p-3 text-left transition ${variant === v.key ? "border-amber-500 bg-amber-50/50 ring-1 ring-amber-200" : "border-border hover:border-neutral-400"}`}
                    data-testid={`widget-builder-variant-${v.key}`}
                  >
                    <div className="text-sm font-semibold">{v.label}</div>
                    <div className="text-[11px] text-neutral-500">{v.desc}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Size */}
            <section>
              <Label className="text-sm font-semibold mb-2">Iframe size</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[11px] text-neutral-500 mb-1">Width</div>
                  <Input value={width} onChange={(e) => setWidth(e.target.value)} className="rounded-xl" data-testid="widget-builder-width" />
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500 mb-1">Height (px)</div>
                  <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} className="rounded-xl" data-testid="widget-builder-height" />
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 mt-1.5">
                Use <code className="bg-neutral-100 px-1 rounded">100%</code> for fluid width inside your container.
              </p>
            </section>
          </div>

          {/* Preview + snippet */}
          <div className="lg:col-span-7 space-y-5" data-testid="widget-builder-preview">
            <div>
              <Label className="flex items-center gap-2 text-sm font-semibold mb-2">
                Live preview
              </Label>
              {/* Iter 69 — "Your site" frame: subtle dashed mock-browser
                  wrapper around the iframe so non-technical partners can
                  instantly tell which part is THEIR page and which part is
                  the embed they're pasting in. */}
              <div className="relative rounded-2xl border-2 border-dashed border-neutral-300 bg-neutral-50/40 p-4 pt-7" data-testid="widget-builder-mock-site">
                <span className="absolute -top-2.5 left-4 px-2 bg-white text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500">
                  Your site
                </span>
                <span className="absolute -top-2.5 right-4 px-2 bg-white text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
                  ← Embedded widget
                </span>
                <iframe
                  key={embedUrl /* force re-mount when config changes */}
                  src={embedUrl}
                  width={String(width).includes("%") ? width : `${parseInt(width, 10) || 100}%`}
                  height={parseInt(height, 10) || 420}
                  frameBorder="0"
                  scrolling="no"
                  style={{ border: 0, maxWidth: "100%", borderRadius: 24 }}
                  title="Search widget preview"
                  loading="lazy"
                  data-testid="widget-builder-iframe"
                />
              </div>
              {/* Iter 69 — "Test full booking flow" CTA. Partners can't
                  click inside the preview iframe (the embed widget's submit
                  target opens a new tab on the parent — useless inside this
                  page). This button gives them a one-click path to walk
                  the entire flow end-to-end with their affiliate_id still
                  attributed. */}
              <a
                href={embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-neutral-300 bg-white text-neutral-700 hover:border-amber-500 hover:text-amber-700 hover:bg-amber-50 transition"
                data-testid="widget-builder-test-flow"
              >
                <Eye className="h-3.5 w-3.5" /> Test full booking flow →
              </a>
            </div>

            <div>
              <Label className="flex items-center gap-2 text-sm font-semibold mb-2">
                <Code2 className="h-4 w-4 text-neutral-700" /> Copy-paste snippet
              </Label>
              <div className="relative">
                <pre className="bg-neutral-900 text-neutral-100 text-[12px] font-mono p-4 rounded-2xl overflow-x-auto whitespace-pre" data-testid="widget-builder-snippet">
{snippet}
                </pre>
                <Button
                  onClick={copyAll}
                  className="absolute top-3 right-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-amber-950"
                  data-testid="widget-builder-copy"
                >
                  {copied ? <><Check className="h-4 w-4 mr-1.5" /> Copied</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy</>}
                </Button>
              </div>
              <p className="text-[11px] text-neutral-500 mt-2">
                Paste this anywhere in your HTML — works on WordPress, Wix, Squarespace, Webflow,
                raw HTML, etc. No script tags, no JS required.
              </p>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 text-sm text-amber-900">
              <div className="font-semibold mb-1">Got more advanced needs?</div>
              <div className="text-amber-800">
                Hosts can also embed a <strong>single-vendor booking widget</strong> from the
                Vendor Dashboard → <em>Widget</em> tab, or use the full{" "}
                <code className="bg-white px-1 rounded">/api/v1</code> REST API with a
                personal API key for programmatic integrations.
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
